<script lang="ts">
import "@/index.css";
import "@cisco-u/button/cu-button.js";
import "@cisco-u/footer/cu-footer.js";
import "@cisco-u/text-select-field/cu-text-select-field.js";
import "@cisco-u/top-header/cu-top-header.js";
import "unfonts.css";

import { defineComponent } from "vue";
import { useCookies } from "vue3-cookies";
import { mapActions, mapGetters, mapMutations } from "vuex";

import CalloutSearchItem from "@/components/CalloutSearchItem.vue";
import cartModal from "@/components/cartModal.vue";
import feedbackBtn from "@/components/feedbackBtn.vue";
import messageBanner from "@/components/messageBanner.vue";
import { FREE } from "@/consts/bundles";
import enrollGenAIModel from "@/layout/GenAIBeta/enrollGenAIModel.vue";
import PageHeader from "@/layout/Header/PageHeader.vue";
import PageNav from "@/layout/Header/PageNav.vue";
import ProfileHeader from "@/layout/Header/profileHeader.vue";
import ProfileList from "@/layout/Header/profileList.vue";
import PersonalInterestSurvey from "@/layout/InterestSurvey/personalInterestSurvey.vue";
import SessionTimeout from "@/layout/SessionTimeout.vue";
import siteWideMessage from "@/layout/siteWideMessage.vue";
import { loginUser, validateBasket } from "@/services/commerceService/products";
import { EventBus } from "@/store/eventBus";
import { sendButtonCtaTelemetry } from "@/utils/ctaTelemetry";
import dateFormats from "@/utils/dateFormats";
import { canShowGenAIModal } from "@/utils/genAIConsent";
import isUnauth from "@/utils/isUnauth";
import { setRedirectUrlCookie } from "@/utils/redirectUrlCookie";

import requiredKeys from "../configs/requiredKeys.json";

const cookies = useCookies().cookies;

export default defineComponent({
  name: "App",
  components: {
    CalloutSearchItem,
    ProfileHeader,
    ProfileList,
    PageHeader,
    PersonalInterestSurvey,
    siteWideMessage,
    PageNav,
    cartModal,
    messageBanner,
    feedbackBtn,
    enrollGenAIModel,
    SessionTimeout,
  },
  data() {
    return {
      scrolled: false,
      showGlobalSuccess: false,
      show: false,
      fixed: false,
      openCartModal: false,
      courseCard: false as boolean,
      contentData: {} as any,
      query: "" as string,
      Communityfooter: {
        type: "Community",
        list: [
          { name: "Communities", url: "/communities", external: false },
          {
            name: "Events",
            url: "/events",
            external: false,
          },
          { name: "Podcasts", url: "/podcasts", external: false },
          {
            name: "Snack Minute Playlist",
            url: "https://www.youtube.com/playlist?list=PL2AuQ_ZjCjsq_dipCVvUS7enTwk4_P7n5",
            external: true,
          },
        ],
      },
      VPfooterInfo: {
        type: "Plans",
        list: [
          {
            name: "Subscriptions",
            url: "/plans",
            external: false,
          },
          {
            name: "Learning Credits",
            url: "https://www.cisco.com/c/en/us/training-events/training-certifications/training/learning-credits.html",
            external: true,
          },
          {
            name: "Request a Demo",
            url: "https://mkto.cisco.com/ciscou-contact-sales.html?utm_campaign=ciscou&utm_source=web-cu&utm_medium=cu-unauth",
            external: true,
          },
        ],
      },
      StoreFooterInfo: {
        type: "Store",
        list: [
          {
            name: "All products",
            url: "/store/all-products",
            external: false,
          },
          {
            name: "Plans",
            url: "/store/plans",
            external: false,
          },
          {
            name: "Learning Credits",
            url: "https://www.cisco.com/c/en/us/training-events/training-certifications/training/learning-credits.html",
            external: true,
          },
          {
            name: "Request a Demo",
            url: "https://mkto.cisco.com/ciscou-contact-sales.html?utm_campaign=ciscou&utm_source=web-cu&utm_medium=cu-unauth",
            external: true,
          },
        ],
      },
      loginText: "Log in with your Cisco ID",
      loginShortText: "Log in",
      showGenAiModal: false,
      labTypes: ["cisco-learning-labs", "cisco-modeling-labs", "graded-labs"],
    };
  },
  watch: {
    $route(): void {
      if (!this.showPageNav) {
        this.scrolled = true;
      }
      this.setupScrollWatch();
      this.checkOnboardSurvey();
      this.setRedirect();
      this.authRedirectToHomepage();
      this.$nextTick(() => {
        document.body.setAttribute("tabindex", "-1");
        document.body.focus({ preventScroll: true });
        document.body.removeAttribute("tabindex");
      });
    },
    achievements(): void {
      this.combineMyLearning();
    },
    myListResults(): void {
      this.combineMyLearning();
    },
    entitlements(): void {
      this.combineMyLearning();
    },
    userStatus(): void {
      this.isGenAIBetaUser();
    },
    routeQuery: {
      immediate: true,
      handler(newVal) {
        if (this.isUnauth()) {
          this.query = newVal;
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      obSurvey: "user/obSurvey",
      userStatus: "user/status",
      showDropDown: "user/showDropDown",
      singleTitleBundles: "pricing/singleTitleBundles",
      essentialsId: "content/essentialsId",
      essentialsBundle: "content/essentialsBundle",
      allAccessBundle: "content/allAccessBundle",
      achievements: "user/achievements",
      myListResults: "user/myListResults",
      entitlements: "user/entitlements",
      systemDowntime: "commerce/systemDowntime",
      announcementBanner: "commerce/announcementBanner",
      showAnyway: "user/showAnyway",
    }),
    joinCiscoText() {
      return `Join ${FREE}`;
    },
    pageHeading(): string {
      return this.$route && (this.$route.meta?.title as string)
        ? (this.$route.meta.title as string)
        : "";
    },
    showProfile(): boolean {
      if (this.$route?.meta?.scrollShowProfile) {
        return true;
      }
      return this.scrolled;
    },
    showManifest(): boolean {
      return this.$route?.name === "manifest";
    },
    showPageNav(): boolean {
      return this.$route?.meta?.pageHeader === true && !this.isUnauth();
    },
    darkHeader(): boolean {
      if (this.isUnauth() && this.$route?.name) {
        return ![
          "certifications",
          "learn",
          "search-all",
          "search-type",
          "all",
          "explore-type",
          "explore-paths",
          "explore-courses",
          "explore-exams",
          "explore-instructorled",
          "explore-tutorials",
          "explore-videos",
          "explore-podcasts",
          "explore-events",
          "explore-challenges",
          "explore-examvouchers",
          "store",
        ].includes(this.$route.name as string);
      }
      return true;
    },
    linkActiveClass(): string {
      return this.darkHeader
        ? "bg-black-darkest text-white-lightest"
        : "bg-white-lightest text-black";
    },
    unauthNav() {
      let nav = [
        { name: "learn", text: "Learn", break: "md" },
        { name: "certifications", text: "Certifications", break: "md" },
        { name: "Explore", text: "Explore", break: "md" },
        {
          name: "community-landing-page",
          text: "Communities",
          break: "custom-2",
        },
        { name: "subscription", text: "Plans" },
        { name: "store", text: "Store" },
      ];

      nav = nav.filter((item) => item.name !== "subscription");
      return nav;
    },
    isPastUtc(): boolean {
      if (this.announcementBanner?.endDate) {
        return dateFormats.isPastUtc(this.announcementBanner.endDate);
      }
      return true;
    },
    isNowWithinRange(): boolean {
      if (
        this.announcementBanner?.startDate &&
        this.announcementBanner?.endDate
      ) {
        return dateFormats.isNowWithinRange(
          this.announcementBanner.startDate,
          this.announcementBanner.endDate
        );
      }
      return false;
    },
    routeQuery() {
      return this.$route?.query?.query || "";
    },
  },

  beforeUnmount(): void {
    document.removeEventListener("keydown", this.handleSkipLinkKey);
  },

  mounted(): void {
    document.addEventListener("keydown", this.handleSkipLinkKey);
    this.navigateReferrerUrl();
    EventBus.on("openCartModal", (content: any) => {
      this.contentData = content.data;
      this.courseCard = content.courseCard;
      this.openCartModal = true;
    });
    EventBus.on("resetModal", () => {
      this.openCartModal = false;
    });
    let configCheck = new Promise((resolve, reject): void => {
      requiredKeys.forEach((element: string) => {
        if (
          typeof window?.env[element as keyof typeof window.env] ==
            "undefined" &&
          !window.location.href.includes("/error")
        ) {
          if (import.meta.env.DEV && !import.meta.env.TEST) {
            console.log(element);
          }
          EventBus.emit("showBanner", {
            type: "ERROR",
            title: "Config Error: ",
            message: "a missing config value was detected " + element,
          });
          reject(new Error("missing config value"));
        }
      });
      resolve(true);
    });

    configCheck.then(
      async () => {
        this.setRedirect();
        this.authRedirectToHomepage();
        this.fetchCertifications();
        if (window.env.ENABLE_REV_UP_RECERT) {
          await this.fetchRevupBundles();
        }

        this.fetchLearnCertification();

        if (!this.isUnauth()) {
          await this.fetchAnnouncementBanner();
          this.fetchObSurvey();
          this.requestUserStatus();
          this.setupScrollWatch();
          this.checkDownTimeAndLogin();
          this.fetchUpcomingClasses();
          this.isGenAIBetaUser();
          this.checkOnboardSurvey();
          try {
            this.fetchMyListData();
            await this.fetchProgress();
          } catch (error) {
            if (import.meta.env.DEV && !import.meta.env.TEST) {
              console.log(error);
            }
          }
        } else {
          await this.fetchUnauthSystemDowntime();
          await this.fetchUnauthAnnouncementBanner();
          await this.fetchSubscriptions();
          await this.fetchAuthorizedContent();
        }
      },
      () => {
        // intenional
      }
    );
  },

  methods: {
    handleSkipLinkKey(e: KeyboardEvent) {
      const skipLink = document.querySelector(
        "[data-cy='skip-to-main-content']"
      );
      if (
        document.activeElement === skipLink &&
        (e.key === "Enter" || e.key === " ")
      ) {
        e.preventDefault();
        this.skipToMainContent();
      }
    },
    setRedirect() {
      if (!this.isUnauth()) return;
      setRedirectUrlCookie(this.$route.fullPath, 1);
    },
    authRedirectToHomepage() {
      if (this.isUnauth() && !this.$route?.meta?.unauth) {
        this.$router.push("/");
      }
    },
    ...mapActions({
      fetchSubscriptions: "pricing/fetchSubscriptions",
      fetchAuthorizedContent: "content/getFreeContent",
      fetchRevupBundles: "content/fetchRevupBundles",
      fetchStatus: "user/fetchStatus",
      fetchMyListData: "user/fetchMyListData",
      fetchProgress: "user/fetchProgress",
      fetchUpcomingClasses: "instance/fetchUpcomingClasses",
      fetchCertifications: "content/fetchCertifications",
      fetchLearnCertification: "content/fetchLearnCertification",
      fetchObSurvey: "user/fetchObSurvey",
      fetchSystemDowntime: "commerce/fetchSystemDowntime",
      fetchAnnouncementBanner: "commerce/fetchAnnouncementBanner",
      fetchUnauthSystemDowntime: "commerce/fetchUnauthSystemDowntime",
      fetchUnauthAnnouncementBanner: "commerce/fetchUnauthAnnouncementBanner",
    }),

    ...mapMutations({
      setMyLearningList: "user/setMyLearningList",
    }),

    navigateReferrerUrl() {
      let referrerUrl = cookies.get("referrer-url");
      if (referrerUrl) {
        referrerUrl = referrerUrl.replace(`${window.location.origin}/`, "");
        if (referrerUrl.includes("unauthenticated.html")) {
          referrerUrl = referrerUrl.replace("unauthenticated.html", "");
        }

        cookies.remove("referrer-url");
        this.$router.push(referrerUrl);
      }
    },

    combineMyLearning() {
      if (
        this.achievements !== null &&
        this.myListResults !== null &&
        this.entitlements !== null
      ) {
        const inProgressList: any[] = this.inProgressHelper(this.achievements);
        const bookmarkList: any[] = this.bookmarkHelper(this.myListResults);
        const singleTitles: any[] = this.singleTitleHelper(this.entitlements);
        const inAssignmentList: any[] = this.inAssignmentHelper(
          this.achievements
        );
        const learningList = [
          ...inProgressList,
          ...singleTitles,
          ...bookmarkList,
          ...inAssignmentList,
        ];

        this.setMyLearningList(learningList);
      }
    },

    inProgressHelper(achievements: any) {
      const inProgressList: any[] = [];
      if (!achievements) return inProgressList;
      Object.keys(achievements)?.forEach((key: string) => {
        if (key && achievements[`${key}`] != null) {
          achievements[`${key}`]["In-progress"]?.forEach((item: any) => {
            if (key === "learningpaths") {
              item.type = "path";
            } else if (key === "tutorials") {
              item.type = "tutorial";
            } else if (key === "courses") {
              item.type = "course";
            }
            inProgressList.push(item);
          });
        }
      });
      return inProgressList?.filter(
        (course: any) =>
          course?.pathStatus !== "STARTED" && course.hiddenByUser !== "Yes"
      );
    },

    bookmarkHelper(myListResults: any) {
      const bookmarkList: any[] = [];
      myListResults?.forEach((item: any) => {
        const bookmarkedItem = item;
        bookmarkedItem.bookmarked = true;
        bookmarkList.push(bookmarkedItem);
      });
      return bookmarkList;
    },

    singleTitleHelper(entitlements: any) {
      const singleTitles: any[] = [];
      entitlements?.forEach((item: any) => {
        const newItem = item;
        if (newItem.is_single) {
          if (newItem.exam_id) {
            newItem.type = "practice-exam";
          } else if (newItem.path_id) {
            newItem.type = "path";
          } else if (
            newItem.content_type &&
            this.labTypes.includes(newItem.content_type)
          ) {
            newItem.type = "lab";
          }
          singleTitles.push(newItem);
        }
      });
      return singleTitles;
    },

    inAssignmentHelper(achievements: any) {
      const inAssignmentList: any[] = [];
      if (achievements?.assigned) {
        achievements.assigned.forEach((item: any) => {
          item.inAssigned = true;
          inAssignmentList.push(item);
        });
      }
      return inAssignmentList;
    },

    requestUserStatus() {
      this.fetchStatus();

      // call this again every 5 minutes
      setInterval(() => {
        this.fetchStatus();
      }, 300000);
    },

    setupScrollWatch(): void {
      if (this.showPageNav) {
        this.scrolled = false;
        window.removeEventListener("scroll", () => {
          // intentional
        });
        window.addEventListener("scroll", this.scrollHeader);
      }
    },

    scrollHeader(): void {
      if (!this.showPageNav) {
        return;
      }
      this.scrolled = document.documentElement.scrollTop >= 438;
    },

    checkOnboardSurvey() {
      // status is loaded thanks to router before route check view-page telem
      if (this.isGenAIConsent()) return;
      this.show =
        this.obSurvey && this.userStatus
          ? !this.userStatus.surveyCompleted
          : false;
      EventBus.emit("updatesurveyCompleted", this.show);
      if (
        this.$route.name == "ForYou" &&
        this.userStatus &&
        !this.userStatus.surveyCompleted
      ) {
        this.fixed = true;
      }
    },

    closeSurvey() {
      this.show = false;
      this.fixed = false;
    },

    returnFeedbackUrl() {
      const userEmail = this.userStatus ? this.userStatus.email : "";
      const userName = this.userStatus ? this.userStatus.display_name : "";
      let envInfo = "";

      switch (window.env.CISCO_U_ENVIRONMENT?.toLowerCase()) {
        case "dev":
          envInfo = "-dev";
          break;
        case "test":
          envInfo = "-test";
          break;
        case "stage":
          envInfo = "-stg";
          break;
      }

      return `https://ciscocx.qualtrics.com/jfe/form/SV_0TH3hJQ3vG2iK3Q?ccoemail=${userEmail}&cconame=${userName}&platform=CiscoU${envInfo}`;
    },
    async checkDownTimeAndLogin() {
      try {
        await this.fetchSystemDowntime();
        this.validateAndLogin();
      } catch (error) {
        if (import.meta.env.DEV && !import.meta.env.TEST) {
          console.log("error fetching system downtime");
        }
      }
    },
    validateAndLogin() {
      if (this.systemDowntime?.status == "ENABLED" || this.isNowWithinRange) {
        return;
      }
      const basketId = localStorage.getItem("BASKET_ID") ?? "NA";
      if (basketId === "NA" || basketId === "") {
        this.loginUser();
      } else {
        validateBasket(basketId)
          .then((data: any) => {
            if (data && data.status != "VALID") {
              this.loginUser();
            }
          })
          .catch((error: any) => {
            if (import.meta.env.DEV && !import.meta.env.TEST) {
              console.log("error validating basket");
            }
          });
      }
    },
    loginUser() {
      loginUser()
        .then((userData: any) => {
          if (userData && userData.basketId != "") {
            localStorage.setItem("BASKET_ID", userData.basketId);
            EventBus.emit("refreshBasket", userData);
          }
        })
        .catch((error: any) => {
          if (import.meta.env.DEV && !import.meta.env.TEST) {
            console.log("error login user to commerce platform");
          }
        });
    },

    updateQuery(newValue: any) {
      this.query = newValue;
    },

    displayConsentManager() {
      if (
        typeof Optanon !== "undefined" &&
        typeof Optanon.ToggleInfoDisplay === "function"
      ) {
        Optanon.ToggleInfoDisplay();
      }
    },

    handleFooterLink(e: any) {
      if (e.detail?.name?.toLowerCase() === "cookie policy") {
        e.detail?.event?.preventDefault();
        this.displayConsentManager();
      }
    },

    handleHeaderSearch(e: any) {
      // Only handle search events for unauthenticated users
      // Authenticated users are handled by PageHeader.vue
      if (!this.isUnauth()) {
        return;
      }

      this.query = e?.detail?.search;
      if (e.detail.search.length >= 2) {
        // Get current query parameters and clean out any existing referrerUrl
        const { referrerUrl, ...currentQuery } = this.$route.query;
        currentQuery.query = e.detail.search;

        // Create clean referrer URL without existing referrerUrl parameter
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete("referrerUrl");
        currentQuery.referrerUrl = currentUrl.toString();

        this.$router.push({
          name: "search-all",
          query: currentQuery,
        });
      }
    },

    isUnauth,

    async sendTelemetry(title: string) {
      if (this.isUnauth()) {
        await sendButtonCtaTelemetry(this.$route.fullPath, title, true);
      }
      window.location.href = window.location.origin + "/login";
    },

    isGenAIBetaUser() {
      //cisco-learning.atlassian.net/browse/LXP-5169
      //disable GenAI modal display which was controlled here
      //unclear if the function is still necessary.
      return;
    },

    isGenAIConsent() {
      return this.userStatus && canShowGenAIModal(this.userStatus);
    },

    hideGenAIModal() {
      this.showGenAiModal = false;
      this.fixed = false;
      this.show = false;
    },

    skipToMainContent() {
      const target = document.getElementById(
        "page-content-start"
      ) as HTMLElement | null;
      if (!target) return;
      target.focus();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  },
});
</script>

<template>
  <div id="application" :class="[{ 'fixed overflow-hidden': fixed }]">
    <a
      href="#page-content-start"
      @mousedown.prevent
      @click.prevent="skipToMainContent"
      class="fixed -top-full left-1/2 -translate-x-1/2 focus:top-3 z-[9999999] inline-flex items-center px-6 py-2 bg-white text-black border border-gray-800 rounded-full text-sm font-medium shadow-lg focus:outline-none hover:underline"
      data-cy="skip-to-main-content"
      >Skip to main content</a
    >
    <messageBanner />
    <session-timeout v-if="!isUnauth()"></session-timeout>
    <router-view v-slot="{ Component }" :key="$route.fullPath">
      <div
        :class="[
          'leading-cisco overflow-x-hidden overflow-y-hidden font-cisco font-light tracking-cisco text-black dark:text-white',
          {
            'pt-0': isUnauth() && !isNowWithinRange,
            'pt-6': isUnauth() && isNowWithinRange,
            'pt-12': !isUnauth() && isNowWithinRange,
          },
        ]"
      >
        <div v-if="!showManifest">
          <profile-header
            v-if="!isUnauth()"
            :dark="false"
            :scrolled="showProfile"
            :show-logo="showProfile"
            :searchQuery="query"
            @close="fixed = false"
            @open="fixed = true"
            @update-query="updateQuery"
          />
          <div
            v-if="isUnauth()"
            :class="[
              'sticky top-0 z-10',
              {
                'bg-white-lightest': darkHeader,
                'bg-black-dark': !darkHeader,
              },
            ]"
          >
            <callout-search-item v-if="darkHeader || showProfile" />

            <cu-top-header
              :dark="!darkHeader"
              :searchtext="query"
              @cu-header-search="handleHeaderSearch"
              logolink="/"
              data-cy="page-header"
            >
              <router-link
                v-for="item in unauthNav"
                :key="item.name"
                slot="nav-item"
                :to="{ name: item.name }"
                active-class="active"
                >{{ item.text }}</router-link
              >

              <cu-button
                slot="action-item"
                size="s"
                :template="!darkHeader ? 'tertiary' : 'secondary'"
                break="sm"
                hide="lg"
                data-cy="login-header-button"
                @click="sendTelemetry(loginText)"
                >{{ loginShortText }}</cu-button
              >
              <cu-button
                slot="action-item"
                size="s"
                :template="!darkHeader ? 'tertiary' : 'secondary'"
                break="lg"
                data-cy="login-header-button"
                @click="sendTelemetry(loginText)"
                >{{ loginText }}</cu-button
              >
              <cu-button
                size="s"
                template="primary"
                slot="action-item"
                break="sm"
                data-cy="join-header-button"
                @click="sendTelemetry(joinCiscoText)"
                >{{ joinCiscoText }}</cu-button
              >

              <router-link
                v-for="item in unauthNav"
                :key="item.name"
                :to="{ name: item.name }"
                v-slot="{ href }"
                custom
              >
                <cu-top-mobile-item
                  slot="mobile-nav-item"
                  :href="href"
                  :active="$route.name === item.name || null"
                  >{{ item.text }}</cu-top-mobile-item
                >
              </router-link>

              <cu-button
                slot="mobile-footer-item"
                size="s"
                :template="!darkHeader ? 'tertiary' : 'secondary'"
                data-cy="login-header-mobile-button"
                @click="sendTelemetry(loginText)"
                >{{ loginText }}</cu-button
              >
              <cu-button
                slot="mobile-footer-item"
                size="s"
                template="primary"
                data-cy="join-header-mobile-button"
                @click="sendTelemetry(joinCiscoText)"
                >{{ joinCiscoText }}</cu-button
              >
            </cu-top-header>
          </div>
        </div>

        <div class="relative">
          <main class="min-h-screen">
            <page-header
              v-if="showPageNav"
              :dark="false"
              :scrolled="showProfile"
              :show-logo="showProfile"
              :searchQuery="query"
              instance="app-not-header"
              @update-query="updateQuery"
              :isScrolled="scrolled"
            />
            <h1 class="sr-only" v-if="pageHeading.length">
              {{ pageHeading }}
            </h1>
            <span
              id="page-content-start"
              tabindex="-1"
              aria-hidden="true"
              class="sr-only"
            ></span>
            <component :is="Component" />

            <div
              class="w-full fixed top-0 z-[1200] pb-6 px-4 text-22 font-light text-white-lightest md:px-6 lg:px-10"
            >
              <div class="relative w-full max-w-[100rem] mx-auto">
                <profile-list
                  v-if="showDropDown && !isUnauth()"
                  @showGlobalSuccess="showGlobalSuccess = true"
                  :show-dropdown="showDropDown"
                />
              </div>
            </div>
            <personal-interest-survey
              v-if="!isUnauth() && (show || showAnyway)"
              data-cy="personal-interest-survey"
              :dark="false"
              :show="show"
              @closePersonalInterestSurvey="closeSurvey"
            />
            <site-wide-message
              :showModal="showGlobalSuccess"
              @hideGlobalSuccess="showGlobalSuccess = false"
            />
            <enrollGenAIModel
              v-if="!isUnauth() && showGenAiModal"
              data-cy="gen-ai-modal"
              :showModel="showGenAiModal"
              @hideGenAIModel="hideGenAIModal()"
            />
          </main>
          <cu-footer
            data-cy="page-footer"
            :_viewPlansLinks="StoreFooterInfo"
            :_communityLinks="Communityfooter"
            :auth="!isUnauth()"
            @cu-footer-link="handleFooterLink"
          >
          </cu-footer>
          <div v-if="$route.name !== 'Path'" class="larger-only">
            <feedbackBtn :url="returnFeedbackUrl()" />
          </div>
        </div>
      </div>
      <div @modalResponse="openCartModal = false">
        <cart-modal
          v-if="openCartModal"
          :content="contentData"
          :singleTitleBundles="singleTitleBundles"
          :course-card="courseCard"
          :essentials-bundle="essentialsBundle"
          :all-access-bundle="allAccessBundle"
          :user-status="userStatus"
          :essentials-id="essentialsId"
        />
      </div>
    </router-view>
  </div>
</template>
<style>
.footer {
  background: #f8f9fa;
  padding: 16px;
  text-align: center;
}
</style>
